import { useNavigate, useSearch } from '@tanstack/react-router';
import React, {
    ReactNode,
    useCallback,
    useLayoutEffect,
    useState,
} from 'react';
import { FullScreenContext } from './FullScreenContext';

export const FullScreenProvider = ({
    children,
    enabled = true,
    portalRef,
}: {
    children: ReactNode;
    enabled?: boolean;
    portalRef: React.RefObject<HTMLDivElement>;
}) => {
    const search = useSearch({
        strict: false,
    }) as { fullscreen?: string };

    const navigate = useNavigate();

    const [isInitialized, setIsInitialized] = useState(false);

    const [fullScreen, setFullScreen] = useState(false);

    useLayoutEffect(() => {
        if (portalRef.current) {
            const fsElement = search.fullscreen
                ? document.getElementById(search.fullscreen)
                : null;

            if (fsElement) {
                setFullScreen(fsElement.dataset.fs === 'true');
            }
            setIsInitialized(true);
        }
        // No need to include search.fullscreen in the dependencies, as we need to set the fullScreen state only once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portalRef]);

    const setIsFullScreen = useCallback(
        (sectionId: string, fullScreen: boolean) => {
            if (fullScreen) {
                navigate({
                    search: { ...search, fullscreen: sectionId },
                    replace: true,
                });
            } else {
                navigate({
                    search: { ...search, fullscreen: undefined },
                    replace: true,
                });
            }
            setFullScreen(fullScreen);
        },
        [navigate, search],
    );

    const isFullScreen = fullScreen && !!search.fullscreen;

    return (
        <FullScreenContext.Provider
            value={{
                portalRef,
                enabled: isInitialized && enabled,
                fullScreenSectionId: search.fullscreen || null,
                isFullScreen,
                setIsFullScreen,
            }}
        >
            <div className={isFullScreen ? 'hidden' : undefined}>
                {children}
            </div>
        </FullScreenContext.Provider>
    );
};
